import { Box, Typography, useTheme } from "@mui/material";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "../../services";
import type { ReferencedLearningActivity } from "../../types";
import { ListItemNavBar } from "../NavigationSidebar/NavigationSidebar.styles";
import { StatusBox } from "../StatusBox/StatusBox";

export function UpcomingActivityItem({
  activity,
  courseId,
  rootId
}: {
  activity: ReferencedLearningActivity
  courseId?: number
  rootId?: number
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const durationLabel = activity.duration
    ? t(activity.duration > 1 ? "minutes_other" : "minutes_one", { count: activity.duration })
    : "";

  const handleClick = () => {
    navigate.toWatch({
      courseId,
      id: activity.id,
      learningObjectTypology: activity.learningObjectTypology,
      rootId
    });
  };

  return (
    <ListItemNavBar
      id={ `sidebar-upcoming-activity-${activity.id}` }
      data-testid="sidebar-upcoming-activity-item"
      disablePadding
      tabIndex={ 0 }
      aria-label={ `${activity.title}. ${t("learning_activity")}${durationLabel ? `. ${durationLabel}` : ""}` }
      onClick={ handleClick }
      onKeyDown={ (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      } }
    >
      <Box
        aria-hidden={ true }
        sx={ {
          alignItems: "center",
          display: "flex",
          gap: "0.5rem",
          justifyContent: "space-between",
          padding: "0.5rem",
          width: "100%"
        } }
      >
        <Box sx={ { minWidth: 0 } }>
          <Typography
            color={ theme.customColors.textPrimary }
            fontSize="0.875rem"
            fontWeight={ 600 }
          >
            { activity.title }
          </Typography>
          <Typography
            color={ theme.customColors.exploreCardText }
            fontSize="0.75rem"
          >
            { t("learning_activity") }{ durationLabel ? ` | ${durationLabel}` : "" }
          </Typography>
        </Box>
        <StatusBox status={ activity.status } isMandatory={ activity.isMandatory } />
      </Box>
    </ListItemNavBar>
  );
}
